import { styled } from "@acko-tech/building-blocks.ui.common";

const TEXT_PRIMARY = "#040222";
const TEXT_SECONDARY = "#36354C";
const TEXT_TERTIARY = "#757575";
const GREEN = "#0FA457";

const GREEN_DARK = "#0B753E";
const PURPLE = "#5920C5";
const PURPLE_50 = "#EFE9FB";
const BORDER = "#E8E8E8";

type PlanCardStyleProps = {
  selected?: boolean;
  variant?: "current" | "extended";
};

type ExtendedStyleProps = {
  isExtended?: boolean;
  isLastCoverageRow?: boolean;
};

type HeaderLabelStyleProps = {
  isHeader?: boolean;
  isLastCoverageRow?: boolean;
};

export const PlansListWrapper = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  margin-top: 2px;

  @media (min-width: 768px) {
    gap: 16px;
    margin-top: 2px;
  }
`;

export const PlanCardButton = styled.div<PlanCardStyleProps>`
  background: #fff;
  border: 1px solid ${(props) => (props.selected ? GREEN : BORDER)};
  border-radius: 12px;
  box-sizing: border-box;
  color: ${TEXT_PRIMARY};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: 100%;

  ${(props) =>
    props.variant === "extended" &&
    `
      background: linear-gradient(180deg, #F6F2FD 0%, #FFFFFF 45%);
    `}

  &:focus-visible {
    outline: 2px solid #1b73e8;
    outline-offset: 2px;
  }
`;

export const CardContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

export const PlanRadioWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
  padding-top: 1px;
`;

export const PlanExtensionTitle = styled.div<{ $isExperiment?: boolean }>`
  color: ${PURPLE};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: ${(props) => (props.$isExperiment ? "16px" : "20px")};
  font-style: normal;
  font-weight: 600;
  line-height: ${(props) => (props.$isExperiment ? "22px" : "28px")};

  @media (min-width: 768px) {
    font-size: ${(props) => (props.$isExperiment ? "16px" : "22px")};
    line-height: ${(props) => (props.$isExperiment ? "22px" : "28px")};
  }
`;

export const PlanPriceRow = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

export const PlanPrice = styled.div<{ $isExperiment?: boolean }>`
  color: ${TEXT_PRIMARY};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: ${(props) => (props.$isExperiment ? "16px" : "20px")};
  font-style: normal;
  font-weight: 600;
  line-height: ${(props) => (props.$isExperiment ? "22px" : "28px")};

  @media (min-width: 768px) {
    font-size: ${(props) => (props.$isExperiment ? "16px" : "22px")};
    line-height: ${(props) => (props.$isExperiment ? "22px" : "28px")};
  }
`;

export const StrikeThroughPrice = styled.div`
  color: #5b5675;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-decoration-line: line-through;
`;

export const PlansExtensionSubTitle = styled.div`
  color: ${TEXT_TERTIARY};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-top: 8px;
  max-width: 248px;

  b,
  span {
    font-weight: inherit;
  }
`;

export const CardDivider = styled.div`
  border-top: 1px dashed #e7e7f0;
  margin: 12px 0 12px;
  width: 100%;
`;

export const BenefitsTitle = styled.div`
  color: ${TEXT_PRIMARY};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 12px;
`;

export const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;

  ${BenefitsTitle} + & {
    margin-top: 0;
  }
`;

export const BenefitItem = styled.div<{ $isExperiment?: boolean }>`
  align-items: flex-start;
  color: ${TEXT_SECONDARY};
  display: flex;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: ${(props) => (props.$isExperiment ? "12px" : "14px")};
  font-weight: 400;
  gap: 8px;
  line-height: ${(props) => (props.$isExperiment ? "18px" : "18px")};
`;

export const BenefitIcon = styled.img`
  flex: 0 0 auto;
  height: 16px;
  margin-top: 1px;
  width: 16px;
`;

export const SavingsStrip = styled.div`
  align-items: center;
  background: linear-gradient(106deg, #f3fdf4 32.1%, #cefffc 110.31%);
  border-top: 1px solid #8ce99a;
  color: var(--Neutral-N600, #040222);
  display: flex;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  justify-content: space-between;
  line-height: 18px;
  min-height: 66px;
  padding: 13px 16px;
  width: 100%;
  box-sizing: border-box;
`;

export const SavingsAmount = styled.span`
  color: ${GREEN_DARK};
  font-size: 18px;
  font-weight: 600;
`;

export const SavingsCta = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  color: var(--button-hyperlink-b-300, #1b73e8);
  cursor: pointer;
  flex: 0 0 auto;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  padding: 0;

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid #1b73e8;
    outline-offset: 2px;
  }
`;

export const ModalContentWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const ModalCloseButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  cursor: pointer;
  height: 24px;
  padding: 0;
  position: absolute;
  right: 0;
  top: -60px;
  width: 24px;

  &::before,
  &::after {
    background: ${TEXT_PRIMARY};
    border-radius: 2px;
    content: "";
    height: 1.5px;
    left: 4px;
    position: absolute;
    top: 11px;
    width: 16px;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid #1b73e8;
    outline-offset: 2px;
  }
`;

export const ComparisonGrid = styled.div`
  display: grid;
  column-gap: 12px;
  grid-template-columns: minmax(100px, 1fr) minmax(100px, 120px) minmax(
      100px,
      120px
    );
  overflow: visible;
  margin-top: 12px;
  width: 100%;

  @media (max-width: 340px) {
    column-gap: 6px;
    grid-template-columns: minmax(72px, 1fr) minmax(84px, 94px) minmax(
        84px,
        94px
      );
  }
`;

export const ComparisonLabel = styled.div<HeaderLabelStyleProps>`
  align-items: ${(props) => (props.isLastCoverageRow ? "flex-start" : "center")};
  border-bottom: ${(props) => (props.isHeader ? "1px solid #e7e7f0" : "0")};
  color: ${TEXT_PRIMARY};
  display: flex;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-weight: ${(props) => (props.isHeader ? 600 : 400)};
  line-height: 18px;
  min-height: ${(props) =>
    props.isHeader ? "64px" : props.isLastCoverageRow ? "64px" : "54px"};
  padding: 8px 6px;
  box-sizing: border-box;
`;

export const ComparisonHeader = styled.div<ExtendedStyleProps>`
  align-items: center;
  background: ${(props) =>
    props.isExtended
      ? "linear-gradient(91deg, #F3FDF4 32.1%, #CEFFFC 110.31%)"
      : "#F8F7FC"};
  border: 1px solid ${(props) => (props.isExtended ? "#E7E7F0" : "#E7E7F0")};
  border-bottom: 1px solid ${(props) => (props.isExtended ? "#E7E7F0" : "#E7E7F0")};
  border-radius: 20px 20px 0 0;
  color: ${TEXT_PRIMARY};
  display: flex;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  justify-content: center;
  line-height: 18px;
  min-height: 64px;
  padding: 8px 10px;
  text-align: center;
  box-sizing: border-box;
`;

export const ComparisonColumn = styled.div<ExtendedStyleProps>`
  align-items: center;
  background: ${(props) =>
    props.isExtended
      ? "linear-gradient(91deg, #F3FDF4 32.1%, #CEFFFC 110.31%)"
      : "#F8F7FC"};
  border-left: 1px solid ${(props) => (props.isExtended ? "#E7E7F0" : "#E7E7F0")};
  border-right: 1px solid ${(props) => (props.isExtended ? "#E7E7F0" : "#E7E7F0")};
  color: ${TEXT_SECONDARY};
  display: flex;
  flex-direction: column;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  justify-content: ${(props) =>
    props.isLastCoverageRow ? "flex-start" : "center"};
  min-height: ${(props) => (props.isLastCoverageRow ? "64px" : "54px")};
  padding: 8px 6px;
  text-align: center;
  box-sizing: border-box;
`;

export const ComparisonPriceLine = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: center;
  line-height: 18px;
`;

export const ComparisonRowPrice = styled.span`
  color: ${TEXT_PRIMARY};
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

export const ComparisonStrikePrice = styled.span`
  color: #5b5675;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
  text-decoration-line: line-through;
`;

export const ComparisonSubText = styled.div`
  color: ${TEXT_SECONDARY};
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
`;

export const ComparisonTotalLabel = styled(ComparisonLabel)`
  border-bottom: 0;
  border-top: 1px solid #e7e7f0;
  font-weight: 500;
  justify-content: flex-start;
  min-height: 64px;
  padding: 28px 6px 8px;
`;

export const ComparisonTotalPrice = styled.div<ExtendedStyleProps>`
  align-items: center;
  background: ${(props) =>
    props.isExtended
      ? "linear-gradient(91deg, #F3FDF4 32.1%, #CEFFFC 110.31%)"
      : "#F8F7FC"};
  border: 1px solid ${(props) => (props.isExtended ? "#E7E7F0" : "#E7E7F0")};
  border-top: 1px solid ${(props) => (props.isExtended ? "#E7E7F0" : "#E7E7F0")};
  border-radius: 0 0 20px 20px;
  color: ${TEXT_PRIMARY};
  display: flex;
  flex-direction: column;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  justify-content: flex-start;
  line-height: 18px;
  min-height: 64px;
  overflow: visible;
  padding: 28px 6px 8px;
  position: relative;
  text-align: center;
  box-sizing: border-box;
`;

export const ComparisonPrice = styled.div`
  background: linear-gradient(129deg, #001a04 10.83%, #3d8a54 100.78%);
  background-clip: text;
  color: transparent;
  -webkit-background-clip: text;
`;

export const ModalSavingsBadge = styled.div`
  align-items: center;
  background: #c1f5c8;
  border: 1.5px solid #ffffff;
  border-radius: 32px;
  box-sizing: border-box;
  color: ${GREEN_DARK};
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  justify-content: center;
  line-height: 16px;
  left: 50%;
  margin-bottom: 0;
  min-height: 20px;
  width: 90px;
  padding: 2px 8px;
  position: absolute;
  top: 0;
  transform: translate(-50%, -50%);
  white-space: nowrap;
`;

export const ComparisonNote = styled.div`
  background: #f9f6ff;
  border: 1px solid ${PURPLE_50};
  border-radius: 8px;
  color: ${TEXT_SECONDARY};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  margin-top: 20px;
  padding: 11px 12px;
`;

export const SeeWhyReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 8px;
  width: 100%;
`;

export const SeeWhyReasonRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  padding: 0;
  width: 100%;
`;

export const SeeWhyReasonIconWrapper = styled.div`
  align-items: center;
  background: #f0f0f6;
  border-radius: 8px;
  display: flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
`;

export const SeeWhyReasonIcon = styled.img`
  flex: 0 0 auto;
  height: 24px;
  width: 24px;
`;

export const SeeWhyReasonContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding-top: 2px;
`;

export const SeeWhyReasonTitle = styled.div`
  color: ${TEXT_PRIMARY};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

export const SeeWhyReasonDescription = styled.div`
  color: ${TEXT_SECONDARY};
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

export const SeeWhyReasonDivider = styled.div`
  border-top: 1px solid #e7e7f0;
  margin: 16px 0;
  width: 100%;
`;

export const SeeWhyInfoNote = styled.div`
  background: #f9f6ff;
  border: 1px solid ${PURPLE_50};
  border-radius: 8px;
  color: #36354c;
  font-family: 'Euclid Circular B', 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  margin-top: 16px;
  padding: 11px 12px;
  width: 100%;
  box-sizing: border-box;
`;
